/**
 * NFL franchise identity colors keyed by abbreviation — public factual team
 * colors (no logos, no licensed assets). Used only for monogram tiles and
 * identity accents; the BarbershopTalk bronze/slate system remains the
 * product brand.
 *
 * Each entry: { bg, accent, text? }.
 *   bg     — primary tile color (a few are darkened from the official shade
 *            purely for text-contrast safety).
 *   accent — secondary color used as a subtle tile edge.
 *   text   — abbreviation color; defaults to near-white, explicitly dark on
 *            light primaries (PIT gold, CIN orange).
 *
 * Meaning never relies on color alone: the abbreviation text always renders.
 * Unknown/historical abbreviations fall back to the neutral slate tile.
 */
const TEAM_COLORS = {
  ARI: { bg: '#97233f', accent: '#ffb612' },
  ATL: { bg: '#a71930', accent: '#a5acaf' },
  BAL: { bg: '#241773', accent: '#9e7c0c' },
  BUF: { bg: '#00338d', accent: '#c60c30' },
  CAR: { bg: '#01579b', accent: '#101820' }, // panther blue, darkened for contrast
  CHI: { bg: '#0b162a', accent: '#c83803' },
  CIN: { bg: '#fb4f14', accent: '#101820', text: '#101820' },
  CLE: { bg: '#311d00', accent: '#ff3c00' },
  DAL: { bg: '#003594', accent: '#869397' },
  DEN: { bg: '#0a2343', accent: '#fb4f14' },
  DET: { bg: '#0076b6', accent: '#b0b7bc' },
  GB:  { bg: '#203731', accent: '#ffb612' },
  HOU: { bg: '#03202f', accent: '#a71930' },
  IND: { bg: '#002c5f', accent: '#a2aaad' },
  JAX: { bg: '#006778', accent: '#d7a22a' },
  KC:  { bg: '#c8102e', accent: '#ffb81c' }, // darkened from #e31837 for contrast
  LAC: { bg: '#0072a5', accent: '#ffc20e' }, // darkened from #0080c6 for contrast
  LAR: { bg: '#003594', accent: '#ffa300' },
  LV:  { bg: '#101820', accent: '#a5acaf' },
  MIA: { bg: '#00747c', accent: '#fc4c02' }, // darkened aqua for contrast
  MIN: { bg: '#4f2683', accent: '#ffc62f' },
  NE:  { bg: '#002244', accent: '#c60c30' },
  NO:  { bg: '#101820', accent: '#d3bc8d' },
  NYG: { bg: '#0b2265', accent: '#a71930' },
  NYJ: { bg: '#125740', accent: '#ffffff' },
  PHI: { bg: '#004c54', accent: '#a5acaf' },
  PIT: { bg: '#ffb612', accent: '#101820', text: '#101820' },
  SEA: { bg: '#002244', accent: '#69be28' },
  SF:  { bg: '#aa0000', accent: '#b3995d' },
  TB:  { bg: '#a50a0a', accent: '#ff7900' }, // darkened from #d50a0a for contrast
  TEN: { bg: '#0c2340', accent: '#4b92db' },
  WAS: { bg: '#5a1414', accent: '#ffb612' },
};

// Alternate/legacy abbreviations map to the same franchise identity.
const ALIASES = {
  WSH: 'WAS',
  JAC: 'JAX',
  LA: 'LAR',
  OAK: 'LV',   // Oakland-era Raiders
  SD: 'LAC',   // San Diego-era Chargers
  STL: 'LAR',  // St. Louis-era Rams
};

const DEFAULT_TEXT = '#f8fafc';

/**
 * Identity colors for an abbreviation, or null when unknown (caller keeps
 * the neutral slate fallback).
 */
export function teamColors(abbreviation) {
  const key = String(abbreviation || '').toUpperCase();
  const entry = TEAM_COLORS[key] || TEAM_COLORS[ALIASES[key]];
  if (!entry) return null;
  return { bg: entry.bg, accent: entry.accent, text: entry.text || DEFAULT_TEXT };
}
